import { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { toast } from 'sonner';
import { Copy, TicketCheck } from 'lucide-react';
import { A } from '@/constants/journey';
import { useLive } from '@/lib/live';

export function copyContract(config) {
  const addr = config?.placeholders?.CONTRACT_ADDRESS || '';
  if (!addr || addr.startsWith('{{')) {
    toast('OFFICIAL CA UNAVAILABLE.', { description: 'Verify the contract through the official Thomas channels.' });
    return;
  }
  const write = navigator.clipboard?.writeText
    ? navigator.clipboard.writeText(addr)
    : Promise.reject();
  write
    .then(() => toast('NUMBER IN YOUR POCKET.', { description: addr }))
    .catch(() => toast('WRONG PLATFORM.', { description: 'Copy failed — select the number manually.' }));
}

export function buyTicket(config) {
  const url = config?.placeholders?.BUY_URL || '';
  if (!url || url.startsWith('{{')) {
    toast('BUY LINK UNAVAILABLE.', { description: 'Verify the official contract and try again.' });
    return;
  }
  window.open(url, '_blank', 'noopener');
}

export const S00Signal = () => {
  const { config, reducedMotion } = useLive();
  const sectionRef = useRef(null);
  const plateRef = useRef(null);
  const bloomRef = useRef(null);
  const steamRef = useRef(null);
  const copyRef = useRef(null);
  const cueRef = useRef(null);

  useLayoutEffect(() => {
    if (reducedMotion) return undefined;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 1.05,
        },
      });
      tl.fromTo(plateRef.current, { scale: 1 }, { scale: 1.09, ease: 'none' }, 0)
        .fromTo(bloomRef.current, { opacity: 0 }, { opacity: 0.85, duration: 0.35 }, 0.05)
        .fromTo(steamRef.current, { opacity: 0.25 }, { opacity: 0.65 }, 0.1)
        .to(copyRef.current, { opacity: 0, y: -40, duration: 0.3 }, 0.62)
        .to(cueRef.current, { opacity: 0, duration: 0.2 }, 0.3);
    }, sectionRef);
    return () => ctx.revert();
  }, [reducedMotion]);

  return (
    <section ref={sectionRef} className="relative h-[230vh]" data-testid="chapter-s00" data-audio-scene="hero" aria-label="The signal turned green">
      <div className="sticky top-0 h-screen overflow-hidden grain">
        <picture>
          <source media="(max-width: 640px)" srcSet={A.plates.s00m} />
          <img
            ref={plateRef}
            src={A.plates.s00d}
            alt="Thomas waits at a wet country station at dawn as the first signal turns green"
            className="absolute inset-0 h-full w-full object-cover will-change-transform"
          />
        </picture>
        <div
          ref={bloomRef}
          className="pointer-events-none absolute left-[52%] top-[18%] h-[46%] w-[34%]"
          style={{ opacity: reducedMotion ? 0.5 : 0, mixBlendMode: 'screen' }}
        >
          <img src={A.fx.bloom} alt="" className="h-full w-full object-contain" />
        </div>
        <img
          ref={steamRef}
          src={A.fx.steamV}
          alt=""
          className="steam-rise pointer-events-none absolute bottom-[8%] left-[2%] w-[42%] opacity-40"
        />
        <div className="scrim-dawn absolute inset-0" />

        <div ref={copyRef} className="absolute bottom-[16%] left-4 max-w-xl sm:left-10">
          <h1 className="font-display text-5xl leading-[0.95] tracking-wide text-[#f3e7cf] sm:text-7xl">
            SOLANA GOT A TRAIN NOW.
          </h1>
          <p className="mt-3 max-w-sm font-sans text-sm leading-relaxed text-[#f3e7cf]/80 sm:text-base">
            Thomas found the CA. That was enough.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => buyTicket(config)}
              className="btn-mech flex h-12 items-center gap-2 rounded-xl bg-[#1f4e8c] px-5 font-mono text-xs tracking-[0.16em] text-[#f3e7cf] ring-1 ring-white/15 hover:bg-[#1b447b]"
              data-testid="buy-ticket-button"
            >
              <TicketCheck size={15} /> BUY $THOMAS
            </button>
            <button
              type="button"
              onClick={() => copyContract(config)}
              className="enamel btn-mech flex h-12 items-center gap-2 rounded-xl px-5 font-mono text-xs tracking-[0.16em] text-[#f3e7cf]"
              data-testid="copy-contract-button"
            >
              <Copy size={15} /> COPY CA
            </button>
          </div>
        </div>

        <div ref={cueRef} className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center">
          <p className="font-mono text-[10px] tracking-[0.34em] text-[#f3e7cf]/70">SCROLL TO DEPART</p>
          <div className="mx-auto mt-2 h-8 w-px bg-gradient-to-b from-[#c9a24a] to-transparent" />
        </div>
      </div>
    </section>
  );
};
